import { useId, useState } from 'react';
import { EmptyState } from '../../../components/PageState';
import { Badge, Button, FilterChips, IconButton, Input, ProgressBar, SectionCard, Select, type ChipOption } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatDateTime, formatRatio } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import { razaoBps } from '../comparar';
import type { EmailCampaign, EmailCampaignStatus } from '../types';
import { EMAIL_STATUS_LABEL, EMAIL_STATUS_TONE, SEGMENT_SHORT_LABEL, normalizarBusca } from '../vocabulario';
import { ColunaOrdenavel } from './ColunaOrdenavel';

/**
 * Lista de campanhas de e-mail (todas as empresas), pronta para escala.
 *
 * Decisoes nao obvias:
 *  1. Busca, empresa e status se combinam; a contagem de cada status
 *     considera a busca e a empresa (faceta), para o chip nunca prometer
 *     linhas que nao aparecem.
 *  2. Ordenar por criada, progresso, abertura ou recuperado; "ver mais" em
 *     lotes de 10. Mudar filtro volta ao primeiro lote.
 *  3. Como a lista cruza empresas, a linha mostra a empresa e o publico
 *     pos-fato: duas campanhas com o mesmo assunto ficariam indistinguiveis.
 *  4. Editar E EXCLUIR so antes de sair (rascunho ou agendada); excluir
 *     pede confirmacao no pai. Campanha que ja saiu tem pagamentos
 *     creditados a ela (ADR-001, ultimo contato): apagar o registro sumiria
 *     com o recuperado e a comissao dos KPIs e com a trilha de quem ganhou o
 *     credito.
 */

interface Acoes {
  readonly onRelatorio: (id: string) => void;
  readonly onEditar: (id: string) => void;
  readonly onExcluir: (id: string) => void;
}

type Coluna = 'criada' | 'progresso' | 'abertura' | 'recuperado';
type FiltroStatus = 'todos' | EmailCampaignStatus;

const LOTE = 10;

function valor(c: EmailCampaign, coluna: Coluna): number | null {
  switch (coluna) {
    case 'criada':
      return new Date(c.createdAt).getTime();
    case 'progresso':
      return razaoBps(c.acceptedCount, c.totalRecipients);
    case 'abertura':
      return razaoBps(c.openedCount, c.acceptedCount);
    case 'recuperado':
      return c.status === 'rascunho' ? null : c.recoveredAmountCents;
  }
}

function Progresso({ campanha }: { readonly campanha: EmailCampaign }): JSX.Element {
  if (campanha.status === 'rascunho') {
    return <p className="text-xs text-ink-subtle">Rascunho: ainda sem público fechado.</p>;
  }
  if (campanha.status === 'agendada') {
    return (
      <p className="text-xs text-ink-subtle">
        {formatCount(campanha.totalRecipients)} destinatários · sai em {formatDateTime(campanha.scheduledFor)}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <ProgressBar value={campanha.acceptedCount} max={campanha.totalRecipients} tone={EMAIL_STATUS_TONE[campanha.status]} label={`Progresso de ${campanha.subject}`} />
      <p className="text-xs tabular-nums text-ink-subtle">
        {formatCount(campanha.acceptedCount)}/{formatCount(campanha.totalRecipients)} · {formatRatio(campanha.acceptedCount, campanha.totalRecipients)}
      </p>
    </div>
  );
}

function Engajamento({ campanha }: { readonly campanha: EmailCampaign }): JSX.Element {
  if (campanha.acceptedCount === 0) {
    return <p className="text-xs text-ink-subtle">—</p>;
  }
  return (
    <div className="text-xs tabular-nums">
      <p className="text-ink">
        {formatRatio(campanha.openedCount, campanha.acceptedCount)} <span className="text-ink-subtle">abriram</span>
      </p>
      <p className="text-ink">
        {formatRatio(campanha.clickedCount, campanha.openedCount)} <span className="text-ink-subtle">clicaram</span>
      </p>
    </div>
  );
}

function Retorno({ campanha }: { readonly campanha: EmailCampaign }): JSX.Element {
  if (campanha.status === 'rascunho' || campanha.status === 'agendada') {
    return <p className="text-xs text-ink-subtle">—</p>;
  }
  return (
    <div>
      <p className="font-medium tabular-nums text-ink">{formatBRLFromCents(campanha.recoveredAmountCents)}</p>
      <p className="text-xs text-ink-subtle">
        {formatCount(campanha.paidCount)} {campanha.paidCount === 1 ? 'pagou' : 'pagaram'}
      </p>
    </div>
  );
}

function AcoesCampanha({ campanha, acoes }: { readonly campanha: EmailCampaign; readonly acoes: Acoes }): JSX.Element {
  const editavel = campanha.status === 'rascunho' || campanha.status === 'agendada';
  return (
    <div className="flex items-center justify-end gap-1">
      <IconButton icon="grafico" label={`Relatório de ${campanha.subject}`} onClick={() => acoes.onRelatorio(campanha.id)} disabled={campanha.status === 'rascunho'} />
      <IconButton icon="editar" label={`Editar ${campanha.subject}`} onClick={() => acoes.onEditar(campanha.id)} disabled={!editavel} />
      <IconButton
        icon="lixeira"
        label={editavel ? `Excluir ${campanha.subject}` : `${campanha.subject} já saiu e tem retorno atribuído: não pode ser excluída`}
        onClick={() => acoes.onExcluir(campanha.id)}
        disabled={!editavel}
        className={editavel ? 'text-status-critical' : ''}
      />
    </div>
  );
}

function TituloCampanha({ campanha, empresa }: { readonly campanha: EmailCampaign; readonly empresa: string }): JSX.Element {
  return (
    <>
      <p className="font-semibold text-ink">{campanha.subject}</p>
      <p className="text-sm text-ink-muted">{campanha.preheader === '' ? '—' : campanha.preheader}</p>
      <p className="mt-0.5 text-xs text-ink-subtle">
        {empresa} · {SEGMENT_SHORT_LABEL[campanha.segmentKey]}
      </p>
    </>
  );
}

export function TabelaDeCampanhasEmail({
  campanhas,
  companies,
  acoes,
  onNova,
}: {
  readonly campanhas: readonly EmailCampaign[];
  readonly companies: readonly CompanySummary[];
  readonly acoes: Acoes;
  readonly onNova: () => void;
}): JSX.Element {
  const buscaId = useId();
  const [busca, setBusca] = useState('');
  const [empresa, setEmpresa] = useState('todas');
  const [status, setStatus] = useState<FiltroStatus>('todos');
  const [ordem, setOrdem] = useState<{ readonly coluna: Coluna; readonly desc: boolean }>({ coluna: 'criada', desc: true });
  const [limite, setLimite] = useState(LOTE);

  const nomeEmpresa = (id: string): string => companies.find((c) => c.id === id)?.name ?? 'Empresa';
  const termo = normalizarBusca(busca.trim());
  const semStatus = campanhas.filter(
    (c) => (empresa === 'todas' || c.companyId === empresa) && (termo === '' || normalizarBusca(`${c.subject} ${c.preheader} ${nomeEmpresa(c.companyId)}`).includes(termo)),
  );
  const filtradas = semStatus.filter((c) => status === 'todos' || c.status === status);
  const ordenadas = [...filtradas].sort((a, b) => {
    const va = valor(a, ordem.coluna);
    const vb = valor(b, ordem.coluna);
    if (va === null && vb === null) {
      return 0;
    }
    if (va === null) {
      return 1;
    }
    if (vb === null) {
      return -1;
    }
    return ordem.desc ? vb - va : va - vb;
  });
  const visiveis = ordenadas.slice(0, limite);
  const contar = (s: EmailCampaignStatus): string => formatCount(semStatus.filter((c) => c.status === s).length);

  const opcoesStatus: readonly ChipOption<FiltroStatus>[] = [
    { value: 'todos', label: 'Todas', count: formatCount(semStatus.length) },
    { value: 'enviando', label: 'Enviando', count: contar('enviando') },
    { value: 'agendada', label: 'Agendadas', count: contar('agendada') },
    { value: 'rascunho', label: 'Rascunhos', count: contar('rascunho') },
    { value: 'concluida', label: 'Concluídas', count: contar('concluida') },
  ];
  const ordenarPor = (coluna: Coluna): void => {
    setOrdem((atual) => (atual.coluna === coluna ? { coluna, desc: !atual.desc } : { coluna, desc: true }));
    setLimite(LOTE);
  };

  return (
    <SectionCard
      id="campanhas-email"
      icon="email"
      title="Campanhas"
      description="Campanhas de e-mail de todas as empresas no período do cabeçalho, sempre para públicos que já passaram pelo fato."
      aside={
        <Badge tone="neutral">
          {formatCount(filtradas.length)} de {formatCount(campanhas.length)}
        </Badge>
      }
      padded={false}
    >
      <div className="flex flex-col gap-3 px-5 pb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex w-full flex-col gap-1 sm:w-72">
            <label htmlFor={buscaId} className="text-xs font-medium text-ink-subtle">
              Buscar
            </label>
            <Input
              id={buscaId}
              size="sm"
              type="search"
              autoComplete="off"
              placeholder="Assunto, preheader ou empresa"
              value={busca}
              onChange={(e) => {
                setBusca(e.target.value);
                setLimite(LOTE);
              }}
            />
          </div>
          <label className="flex w-full flex-col gap-1 text-xs font-medium text-ink-subtle sm:w-56">
            Empresa
            <Select
              size="sm"
              value={empresa}
              onChange={(e) => {
                setEmpresa(e.target.value);
                setLimite(LOTE);
              }}
            >
              <option value="todas">Todas as empresas</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </label>
        </div>
        <FilterChips
          options={opcoesStatus}
          value={status}
          onChange={(v) => {
            setStatus(v);
            setLimite(LOTE);
          }}
          label="Filtrar por status"
          prefix="Status:"
        />
      </div>

      {campanhas.length === 0 ? (
        <div className="px-5 pb-5">
          <EmptyState
            title="Nenhuma campanha no período"
            description="Mude a janela do cabeçalho ou escreva a primeira campanha para um público que já abandonou ou venceu."
            action={
              <Button variant="primary" icon="mais" onClick={onNova}>
                Nova campanha
              </Button>
            }
          />
        </div>
      ) : filtradas.length === 0 ? (
        <div className="px-5 pb-5">
          <EmptyState
            title="Nenhuma campanha com esses filtros"
            description="A busca, a empresa ou o status escondem todas as campanhas."
            action={
              <Button
                variant="secondary"
                size="sm"
                icon="x"
                onClick={() => {
                  setBusca('');
                  setEmpresa('todas');
                  setStatus('todos');
                  setLimite(LOTE);
                }}
              >
                Limpar filtros
              </Button>
            }
          />
        </div>
      ) : (
        <>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-line text-xs text-ink-subtle">
                  <th scope="col" className="py-2 pl-5 pr-3 text-left font-medium uppercase tracking-wide">
                    Campanha
                  </th>
                  <th scope="col" className="px-3 py-2 text-left font-medium uppercase tracking-wide">
                    Status
                  </th>
                  <ColunaOrdenavel coluna="criada" rotulo="Criada em" ativa={ordem.coluna === 'criada'} desc={ordem.desc} onOrdenar={ordenarPor} />
                  <ColunaOrdenavel coluna="progresso" rotulo="Progresso" ativa={ordem.coluna === 'progresso'} desc={ordem.desc} onOrdenar={ordenarPor} className="min-w-[12rem]" />
                  <ColunaOrdenavel coluna="abertura" rotulo="Engajamento" ativa={ordem.coluna === 'abertura'} desc={ordem.desc} onOrdenar={ordenarPor} />
                  <ColunaOrdenavel coluna="recuperado" rotulo="Retorno" ativa={ordem.coluna === 'recuperado'} desc={ordem.desc} onOrdenar={ordenarPor} />
                  <th scope="col" className="px-5 py-2 text-right font-medium uppercase tracking-wide">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map((c) => (
                  <tr key={c.id} className="border-b border-line/70 align-top transition-colors last:border-0 hover:bg-ink/[0.03]">
                    <td className="max-w-md py-3 pl-5 pr-3">
                      <TituloCampanha campanha={c} empresa={nomeEmpresa(c.companyId)} />
                    </td>
                    <td className="px-3 py-3">
                      <Badge tone={EMAIL_STATUS_TONE[c.status]} dot>
                        {EMAIL_STATUS_LABEL[c.status]}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 tabular-nums text-ink-muted">{formatDateTime(c.createdAt)}</td>
                    <td className="px-3 py-3">
                      <Progresso campanha={c} />
                    </td>
                    <td className="px-3 py-3">
                      <Engajamento campanha={c} />
                    </td>
                    <td className="px-3 py-3">
                      <Retorno campanha={c} />
                    </td>
                    <td className="px-5 py-3">
                      <AcoesCampanha campanha={c} acoes={acoes} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="flex flex-col gap-3 px-5 pb-5 md:hidden">
            {visiveis.map((c) => (
              <li key={c.id} className="flex flex-col gap-3 rounded-xl border border-line bg-elevated px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <TituloCampanha campanha={c} empresa={nomeEmpresa(c.companyId)} />
                  </div>
                  <Badge tone={EMAIL_STATUS_TONE[c.status]} dot>
                    {EMAIL_STATUS_LABEL[c.status]}
                  </Badge>
                </div>
                <p className="text-xs text-ink-subtle">Criada em {formatDateTime(c.createdAt)}</p>
                <Progresso campanha={c} />
                <div className="grid grid-cols-2 gap-3">
                  <Engajamento campanha={c} />
                  <Retorno campanha={c} />
                </div>
                <AcoesCampanha campanha={c} acoes={acoes} />
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-3 text-xs text-ink-subtle">
            <span>
              Mostrando {formatCount(visiveis.length)} de {formatCount(ordenadas.length)}
            </span>
            {ordenadas.length > visiveis.length ? (
              <Button variant="secondary" size="sm" icon="chevron" onClick={() => setLimite((l) => l + LOTE)}>
                Ver mais ({formatCount(ordenadas.length - visiveis.length)} restantes)
              </Button>
            ) : null}
          </div>
        </>
      )}
    </SectionCard>
  );
}
